/**
 * WebAuthn (passkey) ceremonie pro administraci.
 *
 * Server posílá options se všemi binárními hodnotami v base64url (tak je serializuje webauthn-lib)
 * a ve stejném formátu je zpátky očekává, takže veškerá práce tady je překlad mezi base64url
 * a ArrayBufferem plus volání `navigator.credentials`.
 *
 * Používá se na dvou místech:
 *   - přihlašovací stránka  → [data-passkey-login]
 *   - zabezpečení účtu      → [data-passkey-register]
 */

interface CeremonyResult {
	error?: string;
	redirect?: string;
}

function base64UrlToBuffer(value: string): ArrayBuffer {
	const padded = value.replace(/-/g, '+').replace(/_/g, '/');
	const binary = atob(padded + '='.repeat((4 - (padded.length % 4)) % 4));
	const bytes = new Uint8Array(binary.length);

	for (let i = 0; i < binary.length; i++) {
		bytes[i] = binary.charCodeAt(i);
	}

	return bytes.buffer;
}

function bufferToBase64Url(buffer: ArrayBuffer): string {
	const bytes = new Uint8Array(buffer);
	let binary = '';

	for (let i = 0; i < bytes.byteLength; i++) {
		binary += String.fromCharCode(bytes[i]);
	}

	return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function toDescriptorList(list: any[] | undefined): PublicKeyCredentialDescriptor[] {
	return (list ?? []).map((descriptor: any) => ({
		...descriptor,
		id: base64UrlToBuffer(descriptor.id),
	}));
}

function toCreationOptions(options: any): PublicKeyCredentialCreationOptions {
	return {
		...options,
		challenge: base64UrlToBuffer(options.challenge),
		user: { ...options.user, id: base64UrlToBuffer(options.user.id) },
		excludeCredentials: toDescriptorList(options.excludeCredentials),
	};
}

function toRequestOptions(options: any): PublicKeyCredentialRequestOptions {
	return {
		...options,
		challenge: base64UrlToBuffer(options.challenge),
		allowCredentials: toDescriptorList(options.allowCredentials),
	};
}

function serializeAttestation(credential: PublicKeyCredential): Record<string, unknown> {
	const response = credential.response as AuthenticatorAttestationResponse;

	return {
		id: credential.id,
		rawId: bufferToBase64Url(credential.rawId),
		type: credential.type,
		response: {
			clientDataJSON: bufferToBase64Url(response.clientDataJSON),
			attestationObject: bufferToBase64Url(response.attestationObject),
			transports: typeof response.getTransports === 'function' ? response.getTransports() : [],
		},
	};
}

function serializeAssertion(credential: PublicKeyCredential): Record<string, unknown> {
	const response = credential.response as AuthenticatorAssertionResponse;

	return {
		id: credential.id,
		rawId: bufferToBase64Url(credential.rawId),
		type: credential.type,
		response: {
			clientDataJSON: bufferToBase64Url(response.clientDataJSON),
			authenticatorData: bufferToBase64Url(response.authenticatorData),
			signature: bufferToBase64Url(response.signature),
			userHandle: response.userHandle === null ? null : bufferToBase64Url(response.userHandle),
		},
	};
}

async function fetchJson(url: string, body?: unknown): Promise<any> {
	const response = await fetch(url, {
		method: body === undefined ? 'GET' : 'POST',
		credentials: 'same-origin',
		headers: {
			'Content-Type': 'application/json',
			'X-Requested-With': 'XMLHttpRequest',
		},
		body: body === undefined ? undefined : JSON.stringify(body),
	});

	if (!response.ok) {
		throw new Error(`Server odpověděl chybou ${response.status}.`);
	}

	return response.json();
}

/**
 * Společná obsluha obou ceremonií — liší se jen tím, co se s options a credentialem udělá.
 */
function bindCeremony(
	root: HTMLElement,
	buttonSelector: string,
	run: (options: any) => Promise<CeremonyResult>,
): void {
	const button = root.querySelector<HTMLButtonElement>(buttonSelector);
	const errorBox = root.querySelector<HTMLElement>('[data-passkey-error]');
	const unsupported = root.querySelector<HTMLElement>('[data-passkey-unsupported]');
	const optionsUrl = root.dataset.optionsUrl;

	if (button === null || optionsUrl === undefined) {
		throw new Error('Passkey blok v šabloně nemá kompletní data atributy.');
	}

	if (window.PublicKeyCredential === undefined) {
		button.classList.add('d-none');
		unsupported?.classList.remove('d-none');

		return;
	}

	const showError = (message: string): void => {
		if (errorBox === null) {
			throw new Error('Passkey blok v šabloně nemá prvek [data-passkey-error].');
		}

		errorBox.textContent = message;
		errorBox.classList.remove('d-none');
	};

	button.addEventListener('click', async () => {
		errorBox?.classList.add('d-none');
		button.disabled = true;

		try {
			const options = await fetchJson(optionsUrl);

			if (typeof options.error === 'string') {
				showError(options.error);

				return;
			}

			const result = await run(options);

			if (typeof result.error === 'string') {
				showError(result.error);

				return;
			}

			if (typeof result.redirect === 'string') {
				window.location.href = result.redirect;

				return;
			}

			throw new Error('Server nevrátil ani chybu, ani cíl přesměrování.');
		} catch (error) {
			// NotAllowedError = uživatel ceremonii zrušil nebo vypršel časový limit.
			const message = error instanceof DOMException && error.name === 'NotAllowedError'
				? 'Ověření bylo zrušeno nebo vypršel čas. Zkus to prosím znovu.'
				: `Passkey se nepodařilo použít: ${error instanceof Error ? error.message : String(error)}`;

			showError(message);
		} finally {
			button.disabled = false;
		}
	});
}

function initLogin(root: HTMLElement): void {
	const verifyUrl = root.dataset.verifyUrl;

	if (verifyUrl === undefined) {
		throw new Error('Passkey přihlášení nemá v šabloně data-verify-url.');
	}

	bindCeremony(root, '[data-passkey-login-button]', async (options) => {
		const credential = await navigator.credentials.get({ publicKey: toRequestOptions(options) });

		if (credential === null) {
			throw new Error('Prohlížeč nevrátil žádný přihlašovací klíč.');
		}

		return fetchJson(verifyUrl, serializeAssertion(credential as PublicKeyCredential));
	});
}

function initRegistration(root: HTMLElement): void {
	const verifyUrl = root.dataset.verifyUrl;

	if (verifyUrl === undefined) {
		throw new Error('Registrace passkey nemá v šabloně data-verify-url.');
	}

	const nameInput = root.querySelector<HTMLInputElement>('[data-passkey-name]');

	bindCeremony(root, '[data-passkey-register-button]', async (options) => {
		const credential = await navigator.credentials.create({ publicKey: toCreationOptions(options) });

		if (credential === null) {
			throw new Error('Prohlížeč nevytvořil žádný přihlašovací klíč.');
		}

		return fetchJson(verifyUrl, {
			name: nameInput?.value ?? '',
			credential: serializeAttestation(credential as PublicKeyCredential),
		});
	});
}

document.addEventListener('DOMContentLoaded', () => {
	document.querySelectorAll<HTMLElement>('[data-passkey-login]').forEach(initLogin);
	document.querySelectorAll<HTMLElement>('[data-passkey-register]').forEach(initRegistration);
});
